import { Avatar, Box, Button, Card, Image, Text } from 'grommet';
import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import SHDLink from '../../helpers/SHDLink';
import PlayerProfileSettings from '@features/PlayerProfileSettings/PlayerProfileSettings';
import { ClaimedPlayerSummary, ClaimedPlayerRole } from './home.d';

interface ClaimedPlayerCardProps {
  player: ClaimedPlayerSummary;
}

const ClaimedPlayerCard = ({
  player,
}: ClaimedPlayerCardProps): React.ReactElement => {
  const [showEditProfile, setShowEditProfile] = useState(false);
  const isAdmin = player.roles.includes(ClaimedPlayerRole.Manager);
  const history = useHistory();

  const getTitle = () => {
    const sport = player && player.primarySport.toLowerCase();
    const primary = player && player.positions?.[sport]?.primary;

    return primary ? primary : '-';
  };

  const handleEditProfile = useCallback(() => {
    setShowEditProfile(false);
    history.push(`/${player.id}`);
  }, [history, player.id]);

  const getSubtitle = () => {
    if (player.yearGraduation) {
      return `Class of ${player.yearGraduation}`;
    } else if (player.collegeCommitName) {
      return `Committed: ${player.collegeCommitName}`;
    }
    return '';
  };

  return (
    <Card
      data-testid="claimed-player-card"
      pad="medium"
      elevation="small"
      gap="large"
    >
      <Box
        direction="row"
        gap="small"
        flex={{ grow: 1, shrink: 0 }}
        fill="horizontal"
      >
        <SHDLink to={`/${player.id}`}>
          {player.imageUrlSmall ? (
            <Avatar src={player.imageUrlSmall} size="medium" />
          ) : (
            <Avatar background="gray-2" size="medium"></Avatar>
          )}
        </SHDLink>
        <SHDLink
          to={`/${player.id}`}
          style={{
            flex: '1 1',
            minWidth: '0px',
          }}
        >
          <Box
            data-testid={`claimed-player-${player.nameFirst}`}
            gap="xsmall"
            flex
          >
            <Box direction="row" gap="small" align="center">
              <Text data-testid="claimed-player-name" weight={'bold'} truncate>
                {player.nameFirst} {player.nameLast}
              </Text>
              {player.isDkPaired && (
                <Box
                  data-testid="dk-paired"
                  height="16px"
                  width="16px"
                  round="full"
                >
                  <Image src="dk_icon.png" fit="contain" />
                </Box>
              )}
            </Box>
            <Box gap="xsmall">
              <Text data-testid="claimed-player-title" truncate>
                {getTitle()}
              </Text>
              <Text data-testid="claimed-player-subtitle" truncate size="small">
                {getSubtitle()}
              </Text>
            </Box>
          </Box>
        </SHDLink>
        <Box align="end" justify="end" width={{ min: '29px' }}>
          {isAdmin && (
            <Button
              data-testid="edit-player-button"
              plain
              label={
                <Text weight="bold" color="tertiary-1">
                  Edit
                </Text>
              }
              onClick={() => {
                setShowEditProfile(true);
              }}
            />
          )}
        </Box>
      </Box>
      {showEditProfile && (
        <PlayerProfileSettings
          closeLayer={() => handleEditProfile()}
          claimedPlayerId={player.id}
        />
      )}
    </Card>
  );
};

export default ClaimedPlayerCard;
